import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Pagination from '../components/Pagination';
import { Mail, Search, CheckCircle, XCircle, Clock, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailLogsPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');

  useEffect(() => {
    if (isSuperAdmin) {
      // Carregar lista de empresas para Super Admin
      api.get('/companies', { params: { page: 1, pageSize: 1000 } })
        .then(({ data }) => setCompanies(data.items || []))
        .catch(() => toast.error('Erro ao carregar empresas'));
    }
  }, [isSuperAdmin]);

  const load = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, pageSize: 20 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (eventFilter !== 'all') params.eventType = eventFilter;
      if (isSuperAdmin && selectedCompany !== 'all') params.companyId = selectedCompany;

      const { data } = await api.get('/email-settings/logs', { params });
      setLogs(data.items || []);
      setMeta(data.meta || { page: 1, pageSize: 20, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar histórico de emails');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search, statusFilter, eventFilter, selectedCompany]);

  const getStatusBadge = (status) => {
    if (status === 'SENT') {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Enviado
        </Badge>
      );
    }
    if (status === 'FAILED') {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="w-3 h-3" /> Falhou
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Pendente
      </Badge>
    );
  };

  const getEventLabel = (event) => {
    const labels = {
      TICKET_CREATED: 'Ticket Criado',
      STATUS_UPDATED: 'Status Atualizado',
      TICKET_ASSIGNED: 'Ticket Atribuído',
      TICKET_FINALIZED: 'Ticket Finalizado',
      SLA_WARNING: 'Alerta de SLA',
      SLA_EXCEEDED: 'SLA Vencido',
      SLA_RISK: 'Risco de SLA',
      CUSTOMER_INFO_REQUEST: 'Solicitação de Info',
      TECHNICAL_REPORT: 'Laudo Técnico',
      PROOF_OF_SERVICE: 'Comprovante',
    };
    return labels[event] || event;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" /> Histórico de Emails
          </h1>
          <p className="text-sm text-slate-500">Visualize todos os emails enviados pelo sistema.</p>
        </div>
      </div>

      {isSuperAdmin && (
        <Card className="p-4 border-slate-200">
          <div className="max-w-md">
            <Label className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4" /> Filtrar por Empresa
            </Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por destinatário ou assunto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="SENT">Enviados</SelectItem>
              <SelectItem value="FAILED">Falhados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Evento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os eventos</SelectItem>
              <SelectItem value="TICKET_CREATED">Ticket Criado</SelectItem>
              <SelectItem value="STATUS_UPDATED">Status Atualizado</SelectItem>
              <SelectItem value="TICKET_ASSIGNED">Ticket Atribuído</SelectItem>
              <SelectItem value="TICKET_FINALIZED">Ticket Finalizado</SelectItem>
              <SelectItem value="SLA_WARNING">Alerta de SLA</SelectItem>
              <SelectItem value="SLA_EXCEEDED">SLA Vencido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Carregando...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Nenhum email encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Data/Hora</th>
                  {isSuperAdmin && <th className="px-4 py-3 font-medium">Empresa</th>}
                  <th className="px-4 py-3 font-medium">Destinatário</th>
                  <th className="px-4 py-3 font-medium">Assunto</th>
                  <th className="px-4 py-3 font-medium">Evento</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Erro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-slate-700 font-medium">
                        {log.company?.name || '-'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-900">{log.recipientEmail || log.to || '-'}</td>
                    <td className="px-4 py-3 text-slate-700">{log.subject}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                        {getEventLabel(log.eventType)}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                    <td className="px-4 py-3 text-xs text-red-600 max-w-xs truncate">
                      {log.errorMessage || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>
    </div>
  );
}
